import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, MessageSquare, Users, Shield, Zap, TrendingUp, CreditCard, Globe, Server, AlertTriangle, ArrowRight, QrCode } from 'lucide-react';
import ContactForm from '../ContactForm';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';
import MultiPurposePopup from '../components/MultiPurposePopup';

const WhatsappApiPage = () => {
  const { t } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const benefitsCards = useMemo(() => {
    const cards = t('whatsapp_api.items', { returnObjects: true });
    const icons = [Users, Zap, Shield, MessageSquare, TrendingUp, CreditCard];
    const colors = [
      { bg: 'bg-blue-100', text: 'text-blue-600' },
      { bg: 'bg-green-100', text: 'text-green-600' },
      { bg: 'bg-purple-100', text: 'text-purple-600' },
      { bg: 'bg-orange-100', text: 'text-orange-600' },
      { bg: 'bg-teal-100', text: 'text-teal-600' },
      { bg: 'bg-red-100', text: 'text-red-600' }
    ];

    const keys = ['centralization', 'automation', 'control', 'distribution', 'analytics', 'sales'];
    return keys.map((key, idx) => ({
      ...cards[key],
      icon: icons[idx],
      color: colors[idx]
    }));
  }, [t]);

  const requirementsSteps = useMemo(() => {
    const reqs = t('whatsapp_api.requirements', { returnObjects: true });
    return [
      { ...reqs.step1, icon: null, id: 1, phase: t('whatsapp_api.requirements.step1.phase', { defaultValue: 'Inicio' }) },
      { ...reqs.step2, icon: Server, id: 2, phase: t('whatsapp_api.requirements.step2.phase', { defaultValue: 'Infraestructura' }) },
      { ...reqs.step3, icon: Globe, id: 3, phase: t('whatsapp_api.requirements.step3.phase', { defaultValue: 'Legal' }) },
      { ...reqs.step4, icon: CreditCard, id: 4, phase: t('whatsapp_api.requirements.step4.phase', { defaultValue: 'Pagos' }) }
    ];
  }, [t]);

  const tableRows = useMemo(() => t('whatsapp_api.table.rows', { returnObjects: true }), [t]);

  return (
    <div className="min-h-screen bg-transparent text-gray-800 font-sans selection:bg-green-100">
      <PageHeader onOpenPopup={openPopup} />
      
      <MultiPurposePopup 
        isOpen={isPopupOpen} 
        onClose={closePopup}
        title={t('whatsapp_api.popup_title')}
        description={t('whatsapp_api.popup_desc')}
      >
        <ContactForm
          formData="inline/4/10oz6w"
          formId="4"
          scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
        />
      </MultiPurposePopup>

      {/* Hero Section */}
      <header className="bg-white border-b border-gray-200 py-24 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {t('whatsapp_api.title').split('+')[0]} <span className="text-green-600">+</span> {t('whatsapp_api.title').split('+')[1]}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('whatsapp_api.subtitle')}
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-20">

        {/* Benefits Section: Grid of Cards */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">{t('whatsapp_api.benefits_title')}</h2>
            <p className="mt-4 text-gray-600">{t('whatsapp_api.benefits_subtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefitsCards.map((card, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 ${card.color.bg} rounded-lg flex items-center justify-center mb-6`}>
                  <card.icon className={`w-6 h-6 ${card.color.text}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
                <p className="text-gray-600 mb-4">{card.desc}</p>
                <ul className="space-y-2 text-sm text-gray-500">
                  {card.points?.map((point, pIdx) => (
                    <li key={pIdx} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline/Steps Section */}
        <section className="relative">
          <div className="text-center mb-16">
             <h2 className="text-3xl font-bold text-gray-900">{t('whatsapp_api.requirements.title')}</h2>
             <p className="mt-4 text-gray-600">{t('whatsapp_api.requirements.subtitle')}</p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {/* Vertical Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200 hidden md:block"></div>

            <div className="space-y-12">
              {requirementsSteps.map((step, idx) => (
                <div key={idx} className="relative flex flex-col md:flex-row items-center justify-between">
                  <div className={`md:w-5/12 ${idx % 2 === 0 ? 'order-2 md:order-1' : 'order-1 md:order-2'} bg-white p-6 rounded-xl shadow-sm border border-gray-100`}>
                    <div className="flex items-center mb-4">
                      {step.tag && <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-0.5 rounded uppercase tracking-wide mr-2">{step.tag}</span>}
                      {step.icon && <step.icon className="w-5 h-5 text-gray-400 mr-2" />}
                      <h3 className="font-bold text-lg">{step.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{step.desc}</p>
                    {step.points && (
                      <ul className="text-xs text-gray-500 list-disc list-inside">
                        {step.points.map((p, pIdx) => <li key={pIdx}>{p}</li>)}
                      </ul>
                    )}
                    {step.warning_title && (
                      <div className="mt-3 bg-yellow-50 p-3 rounded border border-yellow-100 text-xs text-yellow-800">
                        <strong className="block mb-1 flex items-center"><AlertTriangle className="w-3 h-3 mr-1"/> {step.warning_title}</strong>
                        {step.warning_desc}
                      </div>
                    )}
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold border-4 border-white shadow z-10 hidden md:flex">{step.id}</div>
                  <div className={`md:w-5/12 ${idx % 2 === 0 ? 'order-1 md:order-2 text-left pl-8' : 'order-2 md:order-1 text-right pr-8'} hidden md:block`}>
                    <h4 className="text-xl font-bold text-gray-300">{step.phase}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integration Methods Grid */}
        <section className="bg-gray-900 rounded-3xl p-8 md:p-12 text-white">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{t('whatsapp_api.table.title')}</h2>
            <p className="text-gray-400">{t('whatsapp_api.table.subtitle')}</p>
          </div>
          
           <div className="max-w-4xl mx-auto overflow-hidden rounded-xl border border-gray-700 shadow-2xl">
             <div className="overflow-x-auto">
               <table className="min-w-full bg-gray-800 text-left text-sm text-gray-300">
                 <thead className="bg-gray-900 text-xs uppercase font-semibold text-gray-400">
                   <tr>
                     <th className="px-6 py-4">{t('whatsapp_api.table.header_function')}</th>
                     <th className="px-6 py-4">{t('whatsapp_api.table.header_advantage')}</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-700">
                   {tableRows?.map((row, idx) => (
                     <tr key={idx} className="hover:bg-gray-700/50 transition-colors">
                       <td className="px-6 py-4 font-medium text-white">{row.label}</td>
                       <td className="px-6 py-4">{row.val}</td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
          </div>

          <div className="mt-12 text-center border-t border-gray-800 pt-8 flex flex-col items-center gap-6">
             <p className="text-lg">{t('whatsapp_api.final_cta.title')}</p>
             
             <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
               <button onClick={openPopup} className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-full transition-colors duration-200 transform hover:scale-105 shadow-lg shadow-green-900/50">
                 {t('whatsapp_api.final_cta.btn_consult')}
               </button>
               
               <Link to="/whatsapp" className="inline-flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-8 rounded-full transition-all duration-200 border border-gray-600 hover:border-gray-500">
                 <QrCode className="w-5 h-5" />
                 <span>{t('whatsapp_api.final_cta.btn_diff')}</span>
                 <ArrowRight className="w-4 h-4 ml-1" />
               </Link>
             </div>

             <p className="mt-4 text-xs text-gray-500">
               {t('whatsapp_api.final_cta.disclaimer')}
             </p>
          </div>
        </section>

      </main>
    </div>
  );
};

export default WhatsappApiPage;

